import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Divider,
  Dropdown,
  MessageModal,
  MultipleDropdown,
  Spinner,
  TextInput,
  Toggle,
  toggleObject,
} from '@makeably/creativex-design-system';
import {
  addErrorToast,
  addToast,
} from 'components/organisms/Toasts';
import { dateOptionProps } from 'components/reporting/shared';
import { optionArrayProps } from 'components/shared';
import { post } from 'utilities/requests';
import {
  requestCountThirdPartyHistoricalRequestsPath,
  thirdPartyHistoricalRequestsPath,
} from 'utilities/routes';
import styles from './NewHistoricalRequest.module.css';

const props = {
  brands: optionArrayProps.isRequired,
  channels: optionArrayProps.isRequired,
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  markets: optionArrayProps.isRequired,
  partners: optionArrayProps.isRequired,
  publishers: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
};

function formatArray(selected, options) {
  if (selected.length === options.length) return [];

  return selected.map((item) => item.value);
}

function formatCreativeTypes(isImage, isVideo) {
  if (isImage && !isVideo) {
    return ['image'];
  } else if (isVideo && !isImage) {
    return ['video'];
  }

  return [];
}

const NewHistoricalRequest = ({
  brands,
  dateOptions,
  channels,
  markets,
  partners,
  publishers,
}) => {
  const requestDataRef = useRef(null);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [name, setName] = useState(null);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [isImage, setIsImage] = useState(true);
  const [isVideo, setIsVideo] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isGettingRequestCount, setIsGettingRequestCount] = useState(false);
  const [requestCount, setRequestCount] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const publisherOptions = useMemo(() => {
    if (!selectedChannel) return [];

    const channelPublishers = publishers[selectedChannel?.value.toString()] || [];

    return channelPublishers.map((publisher) => {
      const [label, value] = publisher.split('::');

      return {
        label,
        value,
      };
    });
  }, [selectedChannel, publishers]);

  const isNextDisabled = !name
    || !selectedDate
    || (!isImage && !isVideo)
    || selectedBrands.length === 0
    || selectedMarkets.length === 0
    || !selectedChannel
    || (Object.keys(publishers).includes(selectedChannel?.value.toString())
      && selectedPublishers.length === 0);

  const handleNext = async () => {
    setIsConfirming(true);
    setIsGettingRequestCount(true);

    const data = {
      brands: formatArray(selectedBrands, brands),
      channel_id: selectedChannel?.value,
      creative_types: formatCreativeTypes(isImage, isVideo),
      date: selectedDate?.value,
      date_type: selectedDate?.type,
      markets: formatArray(selectedMarkets, markets),
      publishers: selectedPublishers.map((p) => p.value),
    };

    const response = await post(requestCountThirdPartyHistoricalRequestsPath(), data);

    if (!response.isError) {
      requestDataRef.current = data;
      setRequestCount(response.data.count);
      setIsGettingRequestCount(false);
    } else {
      addErrorToast('Error calculating requests, please try again.');
      setIsGettingRequestCount(false);
      setIsConfirming(false);
    }
  };

  const handleConfirm = async () => {
    setIsSaving(true);

    const data = {
      ...requestDataRef.current,
      name,
      partners: selectedPartners.map((partner) => partner.value),
    };

    const response = await post(thirdPartyHistoricalRequestsPath(), data);

    if (!response.isError) {
      addToast('Historical request processing...');
      window.location.href = thirdPartyHistoricalRequestsPath();
    } else {
      addErrorToast('Something went wrong, please try again.');
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsConfirming(false);
    setIsGettingRequestCount(false);
    setRequestCount(null);
    requestDataRef.current = null;
  };

  const renderModalContent = () => {
    if (isGettingRequestCount) {
      return (
        <div className="u-flexRow u-alignCenter u-justifyCenter">
          <Spinner />
        </div>
      );
    }

    return (
      <div className={styles.requestCount}>
        <div className="t-subtitle">
          { `Are you sure you want to send ${requestCount} ads to the following partners for scoring?` }
        </div>
        <ul>
          { selectedPartners.map((partner) => (
            <li key={partner.value}>{ partner.label }</li>
          )) }
        </ul>
      </div>
    );
  };

  return (
    <Card>
      <div className=" u-flexColumn u-gap-24">
        <h5>
          Set the criteria for your historical request. Request count will be provided
          prior to confirmation.
        </h5>
        <Divider />
        <MultipleDropdown
          label="Partners"
          menuProps={{ size: 'medium' }}
          options={partners}
          selected={selectedPartners}
          size="medium"
          onChange={(partner) => setSelectedPartners(toggleObject(selectedPartners, partner))}
          onSelectAll={setSelectedPartners}
        />
        <div className="u-flexRow u-gap-24">
          <TextInput
            label="Name"
            placeholder="Request Name"
            size="large"
            value={name}
            onChange={setName}
          />
          <Dropdown
            label="Date"
            menuProps={{ size: 'medium' }}
            options={dateOptions}
            selected={selectedDate}
            size="medium"
            onChange={setSelectedDate}
          />
        </div>
        <h5>Brand/Market Eligibility</h5>
        <div className="u-flexRow u-gap-24">
          <MultipleDropdown
            label="Brands"
            menuProps={{ size: 'medium' }}
            options={brands}
            selected={selectedBrands}
            size="medium"
            onChange={(brand) => setSelectedBrands(toggleObject(selectedBrands, brand))}
            onSelectAll={setSelectedBrands}
          />
          <MultipleDropdown
            label="Markets"
            menuProps={{ size: 'medium' }}
            options={markets}
            selected={selectedMarkets}
            size="medium"
            onChange={(market) => setSelectedMarkets(toggleObject(selectedMarkets, market))}
            onSelectAll={setSelectedMarkets}
          />
        </div>
        <h5>Channel Eligibility</h5>
        <div className="u-flexRow u-gap-24">
          <Dropdown
            label="Channel"
            menuProps={{ size: 'medium' }}
            options={channels}
            selected={selectedChannel}
            size="medium"
            onChange={setSelectedChannel}
          />
          { publisherOptions.length > 0 && (
            <MultipleDropdown
              label="Publishers"
              menuProps={{ size: 'medium' }}
              options={publisherOptions}
              selected={selectedPublishers}
              size="medium"
              onChange={(pub) => setSelectedPublishers(toggleObject(selectedPublishers, pub))}
              onSelectAll={setSelectedPublishers}
            />
          ) }
        </div>
        <div className="u-flexColumn">
          <div className="t-caption-1 u-marginBottom-16">Asset Type</div>
          <div className="u-flexColumn u-gap-24">
            <Toggle
              checked={isImage}
              label="Image"
              right
              onChange={(e) => setIsImage(e.target.checked)}
            />
            <Toggle
              checked={isVideo}
              label="Video"
              right
              onChange={(e) => setIsVideo(e.target.checked)}
            />
          </div>
        </div>
        <div className="u-buttonGroup u-justifyEnd">
          <Button
            label="Cancel"
            url={thirdPartyHistoricalRequestsPath()}
            variant="tertiary"
          />
          <Button
            disabled={isNextDisabled}
            label="Next"
            onClick={handleNext}
          />
        </div>
      </div>
      <MessageModal
        actionButtonActive={isSaving}
        actionButtonDisabled={isGettingRequestCount}
        actionButtonLabel="Confirm"
        isOpen={isConfirming}
        title={isGettingRequestCount ? 'Calculating Requests...' : 'Confirm Request'}
        onActionButtonClick={handleConfirm}
        onClose={handleCloseModal}
      >
        { renderModalContent() }
      </MessageModal>
    </Card>
  );
};

NewHistoricalRequest.propTypes = props;
export default NewHistoricalRequest;
